import { Spacing } from '@persona-id/design-tokens';
import { CircleAlertIcon } from '@persona-id/design-tokens/icons';
import styled from 'styled-components';

import ConnectedReportMenu from 'dashboard/components/ConnectedReportMenu';
import { ReportInput } from 'dashboard/components/ReportInput';
import ReportModuleHeader from 'dashboard/components/ReportModuleHeader';
import ReportResult from 'dashboard/components/ReportResult';
import Banner from 'dashboard/components-lib/Banner';
import { Report } from 'dashboard/models/report';
import { ReportTemplate } from 'dashboard/models/report-template';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { ApiModel } from 'lib/interfaces/api';

const StyledCardHeader = styled(Card.Title)`
  padding: ${Spacing[5]};
  border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};

  &:not(:only-child) {
    margin-bottom: 0;
  }
`;

interface ReportDetailsProps {
  report: Report;
  reportTemplate?: ReportTemplate;
  multiCardFormat?: boolean;
}

// These reports currently don't support a multi card design (created for Details V2)
// Any newly created reports should support cards
const CARDLESS_REPORTS = [ApiModel.ReportFinraBrokerCheck, ApiModel.ReportSecActionLookup];

// These reports handle input display on their own and do not need the general report input component to be shown.
export const INPUTLESS_REPORTS = [
  ApiModel.ReportProfile,
  ApiModel.ReportBusinessLiensLookup,
  ApiModel.ReportBusinessRegistrationsLookup,
  ApiModel.ReportPoliticallyExposedPerson,
];

const ReportDetails = ({ report, reportTemplate, multiCardFormat = true }: ReportDetailsProps) => {
  const isRedacted = report.attributes['redacted-at'] != null;

  if (isRedacted) {
    return <Banner icon={<CircleAlertIcon />}>This report has been redacted</Banner>;
  }

  const showReportInput = !INPUTLESS_REPORTS.includes(report.type);

  if (CARDLESS_REPORTS.includes(report.type) || multiCardFormat) {
    return (
      <>
        {showReportInput && (
          <Card data-test={`report-details__input--${report.id}`}>
            <Card.Body>
              <Card.Title>Input</Card.Title>
              <ReportInput report={report} isRedacted={isRedacted} />
            </Card.Body>
          </Card>
        )}
        {CARDLESS_REPORTS.includes(report.type) ? (
          <Card data-test={`report-details__results--${report.id}`}>
            <StyledCardHeader>
              <Card.Title>Input</Card.Title>
            </StyledCardHeader>
            <Card.Body>
              <Card.Title>Results</Card.Title>
              <ReportResult
                report={report}
                reportTemplate={reportTemplate}
                isRedacted={isRedacted}
                multiCardFormat={false}
              />
            </Card.Body>
          </Card>
        ) : (
          <ReportResult
            report={report}
            reportTemplate={reportTemplate}
            isRedacted={isRedacted}
            multiCardFormat={multiCardFormat}
          />
        )}
      </>
    );
  } else {
    return (
      <Card data-test={`report-details__module--${report.id}`}>
        <StyledCardHeader
          actions={[<ConnectedReportMenu key='connected-report-menu' report={report} />]}
        >
          <ReportModuleHeader report={report} reportTemplate={reportTemplate} />
        </StyledCardHeader>
        {showReportInput ? (
          <Card.Body>
            <Card.SectionHeader>Input</Card.SectionHeader>
            <ReportInput report={report} isRedacted={isRedacted} />
            <Spacer height={3} />
            <Card.SectionHeader>Results</Card.SectionHeader>
            <ReportResult
              report={report}
              reportTemplate={reportTemplate}
              isRedacted={isRedacted}
              multiCardFormat={multiCardFormat}
            />
          </Card.Body>
        ) : (
          <ReportResult
            report={report}
            reportTemplate={reportTemplate}
            isRedacted={isRedacted}
            multiCardFormat={multiCardFormat}
          />
        )}
      </Card>
    );
  }
};

export default ReportDetails;
